import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Bike,
  Check,
  Copy,
  Loader2,
  Plus,
  ShieldAlert,
  Store,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { PageGuard } from '@components/PageGuard';
import {
  applicationService,
  applicationStatusChip,
  type CreateApplicationBody,
  type RegistrationApplication,
} from '@services/application.service';
import { usePermission } from '@hooks/usePermission';
import { cn } from '@lib/utils';
import { formatTimestamp, humanizeCode, shortId } from '@pages/finance/ledgerFormat';

const TYPE_TABS = [
  { value: '', label: 'All' },
  { value: 'merchant', label: 'Merchants' },
  { value: 'rider', label: 'Riders' },
];

const STATUS_TABS = [
  { value: '', label: 'Awaiting review' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

function apiMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown };
    if (typeof e.message === 'string' && e.message.trim()) return e.message;
  }
  return fallback;
}

/**
 * REGISTRATION QUEUE — who gets to sell or ride on Chekit.
 *
 * Merchants and riders cannot create their own accounts. They apply here, and
 * approving is the moment an account first exists: it provisions the user, the
 * business or rider record, and issues a single-use invite link.
 *
 * The invite link is shown once, on approval, and cannot be retrieved
 * afterwards — with no SMS provider wired yet it is the only way the applicant
 * receives it, so the UI makes copying it unmissable rather than a detail.
 */
export const ApplicationsPage = () => {
  const queryClient = useQueryClient();
  const { hasCheKitPermission } = usePermission();
  const canReview = hasCheKitPermission('businesses.approve');

  const [type, setType] = useState('');
  const [status, setStatus] = useState('');
  const [active, setActive] = useState<RegistrationApplication | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [invite, setInvite] = useState<RegistrationApplication | null>(null);
  const [creating, setCreating] = useState(false);
  // Which row is mid-resend — a single shared mutation's isPending would light
  // up every row's button, so track the id and gate the label/disabled on it.
  const [resendingId, setResendingId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['applications', type, status],
    queryFn: () =>
      applicationService.list({
        type: type || undefined,
        status: status || undefined,
        limit: 200,
      }),
  });

  const rows = useMemo(() => data ?? [], [data]);
  const pendingCount = rows.filter((r) => r.status === 'pending').length;

  const settle = (result: RegistrationApplication, message: string) => {
    toast.success(message);
    setActive(null);
    setReason('');
    setError(null);
    if (result?.inviteLink) setInvite(result);
    queryClient.invalidateQueries({ queryKey: ['applications'] });
  };

  const approveM = useMutation({
    mutationFn: ({ id, note }: { id: string; note?: string }) =>
      applicationService.approve(id, note),
    onSuccess: (r) => settle(r, 'Approved — account created and invite issued.'),
    onError: (e) => setError(apiMessage(e, 'Could not approve this application.')),
  });

  const rejectM = useMutation({
    mutationFn: ({ id, why }: { id: string; why: string }) =>
      applicationService.reject(id, why),
    onSuccess: () => {
      toast.success('Rejected. No account was created.');
      setActive(null);
      setReason('');
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['applications'] });
    },
    onError: (e) => setError(apiMessage(e, 'Could not reject this application.')),
  });

  const resendM = useMutation({
    mutationFn: (id: string) => applicationService.resendInvite(id),
    // settle() opens the invite modal with the fresh link, same as an approval.
    onSuccess: (r) => settle(r, 'Invite resent — a fresh link was issued.'),
    onError: (e) => toast.error(apiMessage(e, 'Could not resend the invite.')),
    onSettled: () => setResendingId(null),
  });

  const busy = approveM.isPending || rejectM.isPending;

  return (
    <PageGuard permission="businesses.read">
      <div className="space-y-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-display text-[32px] font-medium tracking-[-0.02em] text-[var(--text-primary)]">
              Applications
            </h1>
            <p className="mt-1 max-w-2xl text-[14px] text-[var(--text-muted)]">
              Merchants and riders apply here rather than creating their own
              accounts. Approving provisions the account and issues a one-time
              invite link — nothing exists before that.
            </p>
          </div>
          <div className="flex items-center gap-3">
            {pendingCount > 0 && (
              <div className="rounded-2xl bg-[var(--bg-surface)] px-5 py-3 shadow-soft">
                <div className="font-display text-[24px] font-medium leading-none text-[var(--text-primary)]">
                  {pendingCount}
                </div>
                <div className="mt-1 text-[12px] text-[var(--text-muted)]">awaiting review</div>
              </div>
            )}
            {canReview && (
              <button
                type="button"
                onClick={() => setCreating(true)}
                className="inline-flex items-center gap-1.5 rounded-xl bg-[var(--text-primary)] px-4 py-2.5 text-[13px] font-semibold text-[var(--bg-surface)]"
              >
                <Plus className="h-3.5 w-3.5" strokeWidth={1.5} />
                Create directly
              </button>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {TYPE_TABS.map((t) => (
            <Tab key={t.value} active={type === t.value} onClick={() => setType(t.value)}>
              {t.label}
            </Tab>
          ))}
          <span className="mx-1 w-px bg-[var(--border-subtle)]" />
          {STATUS_TABS.map((s) => (
            <Tab key={s.value} active={status === s.value} onClick={() => setStatus(s.value)}>
              {s.label}
            </Tab>
          ))}
        </div>

        {error && !active && (
          <div className="flex items-start gap-2 rounded-2xl bg-[var(--state-danger-soft,rgba(220,38,38,0.08))] px-4 py-3">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-[var(--state-danger)]" strokeWidth={1.5} />
            <p className="text-[13px] text-[var(--state-danger)]">{error}</p>
          </div>
        )}

        <div className="overflow-hidden rounded-3xl bg-[var(--bg-surface)] shadow-soft">
          {isLoading && (
            <div className="flex items-center gap-2 p-12 text-[14px] text-[var(--text-muted)]">
              <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
              Loading applications…
            </div>
          )}

          {!isLoading && rows.length === 0 && (
            <div className="p-12 text-center text-[14px] text-[var(--text-muted)]">
              {status
                ? 'Nothing with that status.'
                : 'Nothing awaiting review — every application has been dealt with.'}
            </div>
          )}

          {rows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-[14px]">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-[0.06em] text-[var(--text-muted)]">
                    <th className="px-6 py-4 font-semibold">Applicant</th>
                    <th className="px-6 py-4 font-semibold">Type</th>
                    <th className="px-6 py-4 font-semibold">What they do</th>
                    <th className="px-6 py-4 font-semibold">Applied</th>
                    <th className="px-6 py-4 font-semibold">Status</th>
                    <th className="px-6 py-4" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-t border-[var(--border-subtle)]">
                      <td className="px-6 py-4">
                        <div className="font-semibold text-[var(--text-primary)]">{r.full_name}</div>
                        <div className="text-[12px] text-[var(--text-muted)]">
                          {r.phone}
                          {r.location_text ? ` · ${r.location_text}` : ''}
                        </div>
                        <div className="text-[11px] text-[var(--text-muted)]">{r.reference}</div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center gap-1.5 text-[var(--text-secondary)]">
                          {r.applicant_type === 'rider' ? (
                            <Bike className="h-3.5 w-3.5" strokeWidth={1.5} />
                          ) : (
                            <Store className="h-3.5 w-3.5" strokeWidth={1.5} />
                          )}
                          {humanizeCode(r.applicant_type)}
                        </span>
                        {r.source === 'internal_bypass' && (
                          // Worth surfacing: this account never went through the
                          // queue, which changes how much scrutiny it has had.
                          <div className="text-[11px] text-[var(--text-muted)]">created by staff</div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-[var(--text-secondary)]">
                        {r.applicant_type === 'rider'
                          ? humanizeCode(r.vehicle_type ?? '—')
                          : r.business_name}
                        {r.applicant_type === 'merchant' && r.business_type && (
                          <div className="text-[12px] text-[var(--text-muted)]">{r.business_type}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-[13px] text-[var(--text-muted)]">
                        {formatTimestamp(r.created_at)}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={cn(
                            'rounded-full px-2.5 py-1 text-[12px] font-semibold',
                            applicationStatusChip(r.status),
                          )}
                        >
                          {humanizeCode(r.status)}
                        </span>
                        {r.status === 'rejected' && r.rejection_reason && (
                          <div className="mt-1 max-w-[16rem] text-[11px] text-[var(--text-muted)]">
                            {r.rejection_reason}
                          </div>
                        )}
                        {r.status === 'approved' && r.created_user_id && (
                          <div className="mt-1 text-[11px] text-[var(--text-muted)]">
                            user {shortId(r.created_user_id)}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right">
                        {canReview && r.status === 'pending' && (
                          <button
                            type="button"
                            onClick={() => {
                              setActive(r);
                              setReason('');
                              setError(null);
                            }}
                            className="rounded-xl bg-[var(--bg-base)] px-3 py-1.5 text-[13px] font-semibold text-[var(--text-primary)]"
                          >
                            Review
                          </button>
                        )}
                        {canReview && r.status === 'approved' && r.created_user_id && (
                          <button
                            type="button"
                            onClick={() => { setResendingId(r.id); resendM.mutate(r.id); }}
                            disabled={resendingId === r.id}
                            className="rounded-xl bg-[var(--bg-base)] px-3 py-1.5 text-[13px] font-semibold text-[var(--text-primary)] disabled:opacity-50"
                            title="Mint a fresh magic link and resend it to the applicant"
                          >
                            {resendingId === r.id ? 'Resending…' : 'Resend invite'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {active && (
        <ReviewModal
          application={active}
          reason={reason}
          setReason={setReason}
          error={error}
          busy={busy}
          onClose={() => setActive(null)}
          onApprove={() => approveM.mutate({ id: active.id, note: reason.trim() || undefined })}
          onReject={() => rejectM.mutate({ id: active.id, why: reason.trim() })}
        />
      )}

      {invite && <InviteModal application={invite} onClose={() => setInvite(null)} />}

      {creating && (
        <CreateDirectModal
          onClose={() => setCreating(false)}
          onCreated={(r) => {
            setCreating(false);
            settle(r, 'Account created and invite issued.');
          }}
        />
      )}
    </PageGuard>
  );
};

/* ── bits ────────────────────────────────────────────────────────────────── */

function Tab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-full px-4 py-2 text-[13px] font-semibold transition-colors',
        active
          ? 'bg-[var(--text-primary)] text-[var(--bg-surface)]'
          : 'bg-[var(--bg-surface)] text-[var(--text-secondary)] hover:bg-[var(--bg-base)]',
      )}
    >
      {children}
    </button>
  );
}

function Field({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <div className="flex justify-between gap-4 text-[13px]">
      <span className="text-[var(--text-muted)]">{label}</span>
      <span className="text-right font-medium text-[var(--text-primary)]">{value}</span>
    </div>
  );
}

function Shell({
  title,
  subtitle,
  children,
  footer,
  onClose,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  footer: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/55 p-4">
      <div className="my-8 w-full max-w-lg rounded-3xl bg-[var(--bg-surface)] shadow-lift">
        <div className="flex items-start justify-between gap-4 border-b border-[var(--border-subtle)] px-6 py-5">
          <div>
            <h2 className="font-display text-[20px] font-medium tracking-[-0.02em] text-[var(--text-primary)]">
              {title}
            </h2>
            {subtitle && <p className="mt-1 text-[13px] text-[var(--text-muted)]">{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-1.5 text-[var(--text-muted)] hover:bg-[var(--bg-base)]"
          >
            <X className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>
        <div className="px-6 py-5">{children}</div>
        <div className="flex items-center justify-end gap-2 border-t border-[var(--border-subtle)] px-6 py-4">
          {footer}
        </div>
      </div>
    </div>
  );
}

function ReviewModal({
  application: a,
  reason,
  setReason,
  error,
  busy,
  onClose,
  onApprove,
  onReject,
}: {
  application: RegistrationApplication;
  reason: string;
  setReason: (v: string) => void;
  error: string | null;
  busy: boolean;
  onClose: () => void;
  onApprove: () => void;
  onReject: () => void;
}) {
  return (
    <Shell
      title={a.full_name}
      subtitle={`${a.reference} · ${humanizeCode(a.applicant_type)}`}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl px-4 py-2 text-[13px] font-semibold text-[var(--text-secondary)]"
          >
            Close
          </button>
          <button
            type="button"
            disabled={busy || !reason.trim()}
            onClick={onReject}
            className="inline-flex items-center gap-1.5 rounded-xl bg-[var(--bg-base)] px-4 py-2 text-[13px] font-semibold text-[var(--state-danger)] disabled:opacity-40"
          >
            <X className="h-3.5 w-3.5" strokeWidth={1.5} />
            Reject
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={onApprove}
            className="inline-flex items-center gap-1.5 rounded-xl bg-[var(--text-primary)] px-4 py-2 text-[13px] font-semibold text-[var(--bg-surface)] disabled:opacity-40"
          >
            {busy ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />
            ) : (
              <Check className="h-3.5 w-3.5" strokeWidth={1.5} />
            )}
            Approve &amp; invite
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="space-y-2 rounded-2xl bg-[var(--bg-base)] p-4">
          <Field label="Phone" value={a.phone} />
          <Field label="Email" value={a.email} />
          <Field label="Location" value={a.location_text} />
          <Field label="Business" value={a.business_name} />
          <Field label="Sells" value={a.business_type} />
          <Field label="Vehicle" value={a.vehicle_type ? humanizeCode(a.vehicle_type) : null} />
          <Field label="Applied" value={formatTimestamp(a.created_at)} />
        </div>

        {error && (
          <div className="flex items-start gap-2 rounded-2xl bg-[var(--state-danger-soft,rgba(220,38,38,0.08))] px-4 py-3">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-[var(--state-danger)]" strokeWidth={1.5} />
            <p className="text-[13px] text-[var(--state-danger)]">{error}</p>
          </div>
        )}

        <div>
          <label className="mb-1.5 block text-[13px] font-medium text-[var(--text-secondary)]">
            Note <span className="text-[var(--text-muted)]">— required to reject</span>
          </label>
          <textarea
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why this is being refused, or a note on the approval"
            className="w-full rounded-xl bg-[var(--bg-base)] px-3 py-2 text-[13px] text-[var(--text-primary)] outline-none"
          />
        </div>

        <p className="text-[12px] text-[var(--text-muted)]">
          Approving creates the account and issues a one-time invite link.
          Rejecting creates nothing at all — the applicant can apply again later.
        </p>
      </div>
    </Shell>
  );
}

/**
 * The link is shown ONCE. It cannot be fetched again, and with no SMS provider
 * wired the applicant has no other way to receive it — so this modal is
 * deliberately hard to dismiss by accident.
 */
function InviteModal({
  application: a,
  onClose,
}: {
  application: RegistrationApplication;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const link = a.inviteLink ?? '';

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success('Invite link copied.');
    } catch {
      toast.error('Could not copy — select the link and copy it manually.');
    }
  };

  return (
    <Shell
      title="Send this link to the applicant"
      subtitle={`${a.full_name} · ${a.phone}`}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            onClick={copy}
            className="inline-flex items-center gap-1.5 rounded-xl bg-[var(--bg-base)] px-4 py-2 text-[13px] font-semibold text-[var(--text-primary)]"
          >
            <Copy className="h-3.5 w-3.5" strokeWidth={1.5} />
            {copied ? 'Copied' : 'Copy link'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl bg-[var(--text-primary)] px-4 py-2 text-[13px] font-semibold text-[var(--bg-surface)]"
          >
            Done
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="rounded-2xl bg-[var(--bg-base)] p-4">
          <code className="block break-all text-[12px] text-[var(--text-primary)]">{link}</code>
        </div>
        <p className="text-[13px] text-[var(--text-secondary)]">
          This is shown once and cannot be retrieved later. Send it to{' '}
          <strong className="font-semibold text-[var(--text-primary)]">{a.phone}</strong> before
          closing — they use it to set a password and sign in. It expires in 24
          hours; after that they need a new invite.
        </p>
      </div>
    </Shell>
  );
}

function CreateDirectModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (r: RegistrationApplication) => void;
}) {
  const [form, setForm] = useState<CreateApplicationBody>({
    applicantType: 'merchant',
    fullName: '',
    phone: '',
  });
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => applicationService.createDirect(form),
    onSuccess: onCreated,
    onError: (e) => setError(apiMessage(e, 'Could not create this account.')),
  });

  const set = (k: keyof CreateApplicationBody, v: string) =>
    setForm((f) => ({ ...f, [k]: v }));

  const isRider = form.applicantType === 'rider';
  const valid =
    form.fullName.trim() &&
    form.phone.trim() &&
    (isRider ? form.vehicleType?.trim() : form.businessName?.trim());

  const input =
    'w-full rounded-xl bg-[var(--bg-base)] px-3 py-2 text-[14px] text-[var(--text-primary)] outline-none';

  return (
    <Shell
      title="Create an account directly"
      subtitle="Skips the queue. The person still sets their own password via the invite."
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl px-4 py-2 text-[13px] font-semibold text-[var(--text-secondary)]"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!valid || create.isPending}
            onClick={() => create.mutate()}
            className="inline-flex items-center gap-1.5 rounded-xl bg-[var(--text-primary)] px-4 py-2 text-[13px] font-semibold text-[var(--bg-surface)] disabled:opacity-40"
          >
            {create.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />}
            Create &amp; invite
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="flex gap-2">
          {(['merchant', 'rider'] as const).map((t) => (
            <Tab
              key={t}
              active={form.applicantType === t}
              onClick={() => setForm((f) => ({ ...f, applicantType: t }))}
            >
              {humanizeCode(t)}
            </Tab>
          ))}
        </div>

        {error && (
          <div className="flex items-start gap-2 rounded-2xl bg-[var(--state-danger-soft,rgba(220,38,38,0.08))] px-4 py-3">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-[var(--state-danger)]" strokeWidth={1.5} />
            <p className="text-[13px] text-[var(--state-danger)]">{error}</p>
          </div>
        )}

        <input className={input} placeholder="Full name"
               value={form.fullName} onChange={(e) => set('fullName', e.target.value)} />
        <input className={input} placeholder="Phone (+2547…)"
               value={form.phone} onChange={(e) => set('phone', e.target.value)} />
        <input className={input} placeholder="Email (optional)"
               value={form.email ?? ''} onChange={(e) => set('email', e.target.value)} />
        <input className={input} placeholder="Town or area (optional)"
               value={form.locationText ?? ''} onChange={(e) => set('locationText', e.target.value)} />

        {isRider ? (
          <select className={input} value={form.vehicleType ?? ''}
                  onChange={(e) => set('vehicleType', e.target.value)}>
            <option value="">Vehicle…</option>
            {['bicycle', 'motorcycle', 'car', 'pickup', 'refrigerated_van', 'truck'].map((v) => (
              <option key={v} value={v}>{humanizeCode(v)}</option>
            ))}
          </select>
        ) : (
          <>
            <input className={input} placeholder="Business name"
                   value={form.businessName ?? ''} onChange={(e) => set('businessName', e.target.value)} />
            <input className={input} placeholder="What do they sell?"
                   value={form.businessType ?? ''} onChange={(e) => set('businessType', e.target.value)} />
          </>
        )}

        <textarea className={input} rows={2} placeholder="Why this is being created directly (recorded)"
                  value={form.notes ?? ''} onChange={(e) => set('notes', e.target.value)} />
      </div>
    </Shell>
  );
}
